import classNames from 'classnames'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'
import { IFormProfileExt } from 'legacy/lut/classes/models/IFormProfileExt'
import { partnerDataStoreService } from 'legacy/lut/classes/services/PartnerDataStoreService'
import { WidgetAddContact } from 'legacy/lut/components/widgets/public/AddContact'
import { CustomFilterContactData } from 'legacy/lut/components/widgets/public/CustomFilterContactData'
import { useChinaPBM } from 'legacy/lut/components/widgets/public/useHooks/useChinaPBM'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { appGetState } from 'legacy/lut/store'
import { lookupGetStateOld, setNextLookupState } from 'legacy/lut/store'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { IForm } from 'core-components/classes/models/IForm'
import { getUrlParam } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Table } from 'core-components/library/list'
import { IRowAction } from 'core-components/library/list/types'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { IDataProviderSort, Static } from 'core-components/types'
import { IDataProviderFilter, Logic, Operator } from 'core-components/types'
import MeasureController from 'core/analytics/MeasureController'
import { HttpCancel } from 'core/classes/http'
import { VisualAudience } from 'core/components/shared/ui/VisualAudience'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { refreshProfilingProgressData } from 'onboarding/features/actions/refreshProgressData'
import { selectRefreshContactDataTrigger } from 'onboarding/features/selectors/selectCommonData'

import { ContactDataCell } from './contactData/Cell'
import { ContactFilterArea } from './contactData/ContactFilterArea'
import { ContactDataRow } from './contactData/Row'
import { WidgetContactEditDialog } from './contactEditDialog/ContactEditDialog'
import { IOnCloseParamas } from './contactEditDialog/models'
import { ContactTileCell } from './contactTileCell/ContactTileCell'
import { useTopPartnerId } from './useHooks/useTopPartnerId'

import styles from './ContactData.module.scss'

interface IContactDataProps {
  crudKey?: string
  flexWidth: string
  onlyOpened?: boolean
  defaultShowNew?: boolean
  onlyMine?: boolean
  countries?: any[]
  pid?: string
  widgetParams: IObjectAny
  hideHeader?: boolean
  selectProfile?: (item: any) => void
  appSource?: string
  disableEditAction?: boolean
  onDataLoad?: (data: DataModel[]) => void
  resetCrudQueries?: boolean // Temp solution
  metrics?: MeasureController
  contactSaved?: IFormProfile | IFormProfileExt | null
  additionalSort?: IDataProviderSort[]
}

export const ContactData = (props: IContactDataProps) => {
  const {
    crudKey,
    flexWidth,
    onlyOpened,
    widgetParams,
    pid,
    defaultShowNew,
    onlyMine,
    selectProfile,
    disableEditAction,
    onDataLoad,
    resetCrudQueries,
    contactSaved,
    additionalSort,
  } = props

  const [mode, setMode] = useState<any>('table')
  const [countries] = useState(lookupGetStateOld().references.countries)
  const [dialogItem, setDialogItem] = useState<DataModel>()
  const [sourceUpdateCount, setSourceUpdateCount] = useState<number>(0)
  const [hasData, setHasData] = useState<boolean>(false)
  const [preferredContacts, setPreferredContacts] = useState<any[]>()
  const topPartnerId = useTopPartnerId(pid || widgetParams.PartnerProId)
  const isChinaPBM = useChinaPBM(pid || widgetParams.PartnerProId)
  const dispatch = useAppDispatch()
  const roles = useSelector(selectUser).roleIds
  const refreshTrigger = useSelector(selectRefreshContactDataTrigger)

  const isBPType = useMemo(() => {
    return getUrlParam('type') === 'bp-registration'
  }, [])

  useEffect(() => {
    partnerDataStoreService.getStaticData(new HttpCancel())
  }, [])

  useEffect(() => {
    crudSource.removeQueries(true)
    setDialogItem(undefined)
  }, [refreshTrigger])

  useEffect(() => {
    if (!topPartnerId) {
      return
    }
    const crud = CrudStore.get(CrudStoreItem.PreferredContacts, `preferred-contact ${crudKey}`)
    crud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'TopPartnerID',
          operator: Operator.EQUAL,
          value: topPartnerId,
        },
        {
          field: 'UserEmail',
          operator: Operator.EQUAL,
          value: appGetState().user.email,
        },
      ],
    })
    crud.setPaging({
      page: 0,
      pageSize: 30,
    })
    crud.fetch().then((result) => {
      setPreferredContacts(result.items.slice())
    })
  }, [crudKey, pid, topPartnerId, widgetParams.PartnerProId])

  const crudSource = useMemo(() => {
    if ((pid || widgetParams.PartnerProId) && !topPartnerId) {
      return new Crud<any>({
        fetcher: new Static([] as any[]),
        keyField: 'EntryID',
      })
    }

    const { ContactExtData, OpenedContactRequests, ContactAllData } = CrudStoreItem

    const cacheKey = `contact-list ${crudKey}`

    const otherSourceName = onlyOpened ? OpenedContactRequests : ContactAllData

    const sourceName = defaultShowNew ? ContactExtData : otherSourceName

    const source = CrudStore.get(sourceName, cacheKey)

    if (resetCrudQueries) {
      source.removeQueries(true)
    }

    const filterByPartnerIdAndContactIdAndStatus = {
      logic: Logic.AND,
      filters: [
        {
          logic: Logic.OR,
          filters: [
            {
              field: 'PartnerID',
              operator: Operator.EQUAL,
              value: pid || widgetParams.PartnerProId,
            },
            {
              field: 'NewPartnerID',
              operator: Operator.EQUAL,
              value: pid || widgetParams.PartnerProId,
            },
          ],
        },
        {
          field: 'ContactID',
          operator: Operator.STARTSWITH,
          value: 'new-',
        },
        {
          field: 'WorkflowStatus',
          operator: Operator.NOTEQUAL,
          value: 'Done',
        },
      ],
    }

    const filterByEntryId = {
      logic: Logic.AND,
      filters: [
        {
          field: 'EntryID',
          operator: Operator.ISNOTNULL,
          value: '',
        },
      ],
    }

    const filterByPartnerIdOrTopPartnerId = {
      logic: Logic.AND,
      filters: [
        //Y22-983: HQ registration but list of users contains branch userscontacts too
        {
          field: isBPType ? 'PartnerID' : 'TopPartnerId',
          operator: Operator.EQUAL,
          value: isBPType
            ? pid || widgetParams.PartnerProId
            : topPartnerId || pid || widgetParams.PartnerProId,
        },
      ],
    }

    const otherFilters = onlyOpened ? filterByEntryId : filterByPartnerIdOrTopPartnerId

    const defaultFilter = defaultShowNew ? filterByPartnerIdAndContactIdAndStatus : otherFilters

    if (onlyOpened && (pid || widgetParams.PartnerProId)) {
      defaultFilter.filters.push({
        field: 'PartnerID',
        operator: Operator.EQUAL,
        value: pid || widgetParams.PartnerProId,
      })
    }

    source.setDefaultFilter(defaultFilter)

    const partnerId = pid || widgetParams.PartnerProId
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [],
    }

    const lookupOld = lookupGetStateOld()

    if (lookupOld.searchResultContactId) {
      filter.filters.push({
        field: 'ContactID',
        operator: Operator.EQUAL,
        value: lookupOld.searchResultContactId,
      })
      setNextLookupState({
        ...lookupOld,
        searchResultContactId: '',
      })
    }

    if (!defaultShowNew && !onlyOpened && topPartnerId !== partnerId) {
      filter.filters.push({
        field: 'PartnerID',
        operator: Operator.EQUAL,
        value: partnerId,
      })
    }
    if (onlyOpened && (!defaultShowNew || onlyMine)) {
      filter.filters.push({
        field: 'ModifiedBy',
        operator: Operator.EQUAL,
        value: appGetState().user.email,
      })
    }
    source.setFilter(filter)

    let sort: IDataProviderSort[] = [
      {
        field: 'IsKeyContact',
        dir: 'desc',
      },
      {
        field: 'LastName',
        dir: 'asc',
      },
    ]
    if (additionalSort) {
      sort = [...sort, ...additionalSort]
    }
    source.setSort(sort)

    return source
  }, [
    pid,
    widgetParams.PartnerProId,
    topPartnerId,
    defaultShowNew,
    crudKey,
    onlyOpened,
    resetCrudQueries,
    isBPType,
    onlyMine,
    additionalSort,
  ])

  const [crudObj] = useState({ crud: crudSource })
  crudObj.crud = crudSource

  useEffect(() => {
    const fetch = async () => {
      const data = await crudSource.fetch()
      setHasData(data.total > 0)
      if (contactSaved) {
        crudSource.updateItem({ models: contactSaved, keyField: 'EntryId' })
      }
    }
    fetch()
  }, [crudSource, contactSaved])

  const [dialogItemObj] = useState({ dialogItem })
  dialogItemObj.dialogItem = dialogItem

  useEffect(() => {
    const crud = CrudStore.get(CrudStoreItem.ContactAllData)
    return crud.subscribe('change', () => {
      setSourceUpdateCount((value) => value + 1)
      if (dialogItemObj.dialogItem && dialogItemObj.dialogItem.get('ContactID')) {
        crudObj.crud
          .getById({ id: dialogItemObj.dialogItem.get('ContactID'), keyField: 'ContactID' })
          .then((res: IForm) => {
            if (res && dialogItemObj.dialogItem && dialogItemObj.dialogItem.get('ContactID')) {
              setDialogItem(new DataModel(res))
            }
          })
      }
    })
  }, [crudObj.crud, dialogItemObj.dialogItem])

  const columns = useMemo(() => {
    const p = countries ? 2 : 0
    const onlyOpenedSpace = onlyOpened ? 4 : 0

    const countriesColumns = [
      {
        field: 'CountryOfBusiness',
        title: 'Country Of Business',
        priority: 5,
        customFilter: CustomFilterContactData,
      },
    ]

    const generalColumns = [
      { field: 'ContactID', title: 'Contact ID', priority: 1 },
      { field: 'LearnerID', title: 'Learner ID', priority: 2 },
      { field: 'FirstName', title: 'First Name', priority: 3 },
      { field: 'LastName', title: 'Last Name', priority: 4 },
      {
        field: 'PR_HP_ROLE',
        title: 'Hp Roles',
        priority: p + onlyOpenedSpace + 5,
      },
      {
        field: 'UserStatus',
        title: 'User Status',
        priority: p + onlyOpenedSpace + 6,
      },
      {
        field: 'WorkPhone',
        title: 'Work Phone',
        priority: p + onlyOpenedSpace + 7,
      },
      {
        field: 'MobilePhone',
        title: 'Mobile Phone',
        priority: p + onlyOpenedSpace + 8,
      },
      { field: 'Email', title: 'Email', priority: p + onlyOpenedSpace + 9 },
      {
        field: 'LocationID',
        title: 'Location ID',
        priority: p + onlyOpenedSpace + 10,
      },
      {
        field: 'NeverCall',
        title: 'Never Call',
        priority: p + onlyOpenedSpace + 11,
      },
      {
        field: 'NeverEmail',
        title: 'Never Email',
        priority: p + onlyOpenedSpace + 12,
      },
      {
        field: 'PartnerID',
        title: 'Partner ID',
        priority: p + onlyOpenedSpace + 13,
      },
      {
        field: 'CommunicationName',
        title: 'Comm. Name',
        priority: p + onlyOpenedSpace + 14,
      },
      { field: 'City', title: 'City', priority: p + onlyOpenedSpace + 15 },
      {
        field: 'PrimaryPartner',
        title: 'Primary',
        priority: p + onlyOpenedSpace + 16,
      },
    ]

    const newColumns = [
      { field: 'CreatedBy', title: 'Created By', priority: p + 16 },
      { field: 'Created', title: 'Created', priority: p + 17 },
    ]

    const oldColumns = [
      {
        field: 'TopPartnerID',
        title: 'TopPartnerID',
        priority: p + onlyOpenedSpace + 18,
      },
      { field: 'ModifiedBy', title: 'Modified By', priority: p + 19 },
      { field: 'Modified', title: 'Modified', priority: p + 17 },
    ]

    const onlyOpenedColumns = [
      { field: 'WorkflowStatus', title: 'Workflow status', priority: p + 5 },
      {
        field: 'ProfileTeamComment',
        title: 'Profile team comment',
        priority: p + 6,
      },
      { field: 'Market', title: 'Market', priority: p + 8 },
    ]

    let general = [...generalColumns]
    if (countries) {
      general = [...countriesColumns, ...generalColumns]
    }
    let result = [...general, ...oldColumns]
    if (onlyOpened) {
      result = [...general, ...oldColumns, ...onlyOpenedColumns]
      if (defaultShowNew) {
        result = [...general, ...newColumns, ...onlyOpenedColumns]
      }
    }
    return result
  }, [countries, defaultShowNew, onlyOpened])

  const togglePreferredContact = async (item: DataModel) => {
    if (preferredContacts) {
      const newPreferredContacts = preferredContacts.slice()
      const crud = CrudStore.get(CrudStoreItem.PreferredContacts, 'preferred-contact-list')
      const findedIndex = preferredContacts.findIndex(
        (el) => el.ContactId === item.get('ContactID')
      )
      if (~findedIndex) {
        crud.delete('638', preferredContacts[findedIndex]).catch(Logger.error)
        newPreferredContacts.splice(findedIndex, 1)
        setPreferredContacts(newPreferredContacts)
      } else {
        crud
          .create('638', [
            {
              ContactId: item.get('ContactID'),
              PartnerId: item.get('PartnerID'),
              TopPartnerId: item.get('TopPartnerId') || item.get('TopPartnerID'),
              UserEmail: appGetState().user.email,
            },
          ])
          .then((result) => {
            newPreferredContacts.push(result[0])
            setPreferredContacts(newPreferredContacts)
          })
      }
    }
  }

  const getRowActions = () => {
    const rowActions: IRowAction<DataModel>[] = []

    if (!disableEditAction) {
      rowActions.push({
        id: 'edit',
        action: (item) => setDialogItem(item),
        title: 'Edit',
        icon: 'kbase kbase-edit kbase-sm',
      })
    }
    if (preferredContacts) {
      rowActions.unshift({
        id: 'favorite',
        action: (item) => togglePreferredContact(item),
        title: 'Preferred',
        icon: (item) => {
          const isPreferred = preferredContacts.find(
            (el) => !el.DELETED && el.ContactId === item.get('ContactID')
          )
          return isPreferred ? 'kbase kbase-star-alt' : 'kbase kbase-star'
        },
      })
    }

    if (selectProfile) {
      rowActions.unshift({
        id: 'Select',
        action: (item) => selectProfile(item),
        title: 'Select',
        icon: 'kbase kbase-add',
      })
    }
    return rowActions
  }

  const cacheKeyColumns = `contact-cache-${countries ? 'countries-' : ''}${
    onlyOpened ? `oo-${defaultShowNew ? 'def-' : ''}` : ''
  }`

  const ignoredFields = ['EntryID']

  if (!defaultShowNew) {
    ignoredFields.push('ContactID')
  }

  return (
    <>
      {!props.hideHeader ? (
        <>
          <div className={styles.headerActions}>
            <Button
              className={styles.headerAction}
              variant={'text'}
              color={'grey'}
              iconButton={classNames(styles.headerAction__icon, 'hpicon hpicon-th')}
              onClick={() => setMode('div')}
            />
            <Button
              className={styles.headerAction}
              variant={'text'}
              color={'grey'}
              iconButton={classNames(styles.headerAction__icon, 'hpicon hpicon-list')}
              onClick={() => setMode('table')}
            />
          </div>
          <ContactFilterArea
            source={crudSource}
            ignoredFields={ignoredFields}
            key={`f_pid_${!!topPartnerId}`}
          />
        </>
      ) : null}
      {mode === 'div' && hasData && (
        <div className={styles['add-contact-wrap']}>
          <div className={styles['add-contact-wrap__card']}>
            <WidgetAddContact
              PartnerID={pid || widgetParams.TopPartnerID}
              cardView
              appSource={props.appSource}
            />
          </div>
        </div>
      )}
      <Table<DataModel>
        sourceUpdateCount={sourceUpdateCount}
        key={`pid_${!!topPartnerId}`}
        className={classNames(
          mode === 'div' && hasData ? styles.tableFlex : '',
          'cmp-contactDataNew-table'
        )}
        header={mode !== 'div'}
        pagination
        hasExport={!isChinaPBM}
        onDataLoad={onDataLoad}
        exportTooltip={() => (
          <>
            Export capability is provided as a value add in Click & Go. Exported data is not
            intended to be used for any type of mass communication efforts
          </>
        )}
        cacheKey={cacheKeyColumns}
        mode={mode}
        source={crudSource}
        columns={columns}
        flexWidth={flexWidth}
        convertIFormToP={(data) => data.map((i) => new DataModel(i))}
        rowTpl={(item, renderChildren, rowState, rowAttrs, additional) => {
          return mode === 'table' ? (
            <ContactDataRow
              key={item.get('EntryID')}
              rowAttrs={rowAttrs}
              item={item}
              renderChildren={renderChildren}
              rowState={rowState}
              additional={additional}
              colsNumber={columns.length}
            />
          ) : (
            <ContactTileCell item={item} />
          )
        }}
        cellTpl={
          mode === 'table'
            ? (item, column, cellAttrs) => {
                return <ContactDataCell item={item} column={column} cellAttrs={cellAttrs} />
              }
            : undefined
        }
        rowActions={mode === 'table' ? getRowActions() : undefined}
      />
      {dialogItem && dialogItem.get('ContactID') && (
        <VisualAudience<any>
          read={['Read Only']}
          write={roles.filter((el: string) => el !== 'Read Only')}
          component={WidgetContactEditDialog}
          dialogEntryId={dialogItem.get('ContactID')}
          entryIdItem={dialogItem}
          appSource={props.appSource}
          onClose={(params: IOnCloseParamas) => {
            crudSource.removeQueries(true)
            setDialogItem(undefined)
            if (params?.isUpdated) {
              dispatch(refreshProfilingProgressData())
            }
          }}
          onboardingRequestId={widgetParams.onboardingRequestId}
        />
      )}
    </>
  )
}

export default ContactData
